import { Box, Button, Chip, Grid, Tooltip } from '@mui/material'
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest'
import React from 'react'
import { TFunction } from 'i18next'
import { UnStyledLink } from '../../components/layout/styled'
import theme from '../../styles/theme'
import { displayDateAsText } from '../../utils/displayDateAsText'
import { useAppTranslations } from '../../utils/hooks/useAppTranslations'
import {
  ActivityFeedGroupPartsFragment,
  ActivityFeedOrigin,
  ActivityFeedPartsFragment,
  ActivityType,
  EntityType,
  Maybe,
  PartialUser,
} from '../../__generated__/graphql'
import { ACTIVITY_FEED_MAX_VISIBLE_ITEM_CHANGES } from '../../env'
import { activityFeedConf } from './activityFeedConf'
import {
  getActivityFeedAddRemovePropertyLabelInSentence,
  getActivityFeedPropertyLabel,
  getActivityFeedValueDisplay,
  isActivityFeedAdTemplateNativeTemplateUpdate,
  getActivityFeedGeoTargetingTranslationKey,
  isActivityFeedPropertyClearedUpdate,
  isActivityFeedImageUpdate,
  isActivityFeedRedirectUrlUpdate,
  isActivityFeedUserPasswordUpdate,
  isActivityFeedZoneProperty,
  isDirectoryPresetListProperty,
  isExclusionPresetListProperty,
  parseActivityFeedZoneAssignment,
} from './activityFeedDisplay'
import {
  getActivityFeedEntityType,
  getActivityFeedEntityTypeLabel,
  getActivityFeedEntityTypeLabelInSentence,
  getActivityFeedMainEntityDisplay,
  isActivityFeedCompanyEntityType,
} from './activityFeedEntity'
import { CompanyRouteBuilder } from '../routeParams'
import pwsLogo from '../../img/logo_powerspace_spinner.svg'

export const DATE_FORMAT = 'dddd DD MMMM YYYY - HH:mm'

const POWERSPACE_COMPANY_SLUG = 'powerspace'

const activityFeedChangesTooltipSlotProps = {
  tooltip: {
    sx: {
      maxWidth: 480,
      maxHeight: 360,
      overflow: 'auto',
    },
  },
}

const PowerspaceLogo: React.FC<{ alt: string }> = ({ alt }) => (
  <Tooltip title={alt}>
    <Box
      component="img"
      src={pwsLogo}
      alt={alt}
      sx={{ height: 18, width: 18, display: 'inline-block', verticalAlign: 'middle' }}
    />
  </Tooltip>
)

const ActivityFeedRow: React.FC<{
  group: ActivityFeedGroupPartsFragment
  index: number
  showAllChanges?: boolean
}> = ({ group, index, showAllChanges = false }) => {
  const { user, entity, company, createdAt, origin } = group
  const firstItem = group.items[0]
  const mainEntityContext = {
    entity,
    company,
    entityType: firstItem?.entityType,
    entityId: firstItem?.entityId,
    items: group.items,
  }
  const mainEntity = getActivityFeedMainEntityDisplay(mainEntityContext)
  const entityType = getActivityFeedEntityType(entity, firstItem?.entityType)
  const entityUrl = mainEntity.url

  return (
    <Grid
      container
      spacing={1}
      sx={(theme) => ({
        backgroundColor: index % 2 === 0 ? theme.palette.background.paper : theme.dataTable.standardZebraBackground,
        borderLeft: 2,
        borderColor: entityType ? activityFeedConf[entityType]?.backgroundColor : undefined,
        borderBottom: 2,
        borderBottomColor: theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[200],
        marginTop: 1,
        marginBottom: 0,
        paddingTop: 1,
        paddingBottom: 1,
        fontSize: theme.typography.body2.fontSize,
      })}
    >
      <UserSection user={user} origin={origin} />
      <EntitySection entityType={entityType} entityUrl={entityUrl} />
      <TypeSection
        group={group}
        entityUrl={entityUrl}
        entityLabel={mainEntity.label}
        entityType={entityType}
        showAllChanges={showAllChanges}
      />
      <CompanySection company={company} />
      <DateSection date={String(createdAt)} />
    </Grid>
  )
}

export default ActivityFeedRow

const UserSection: React.FC<{
  user: Maybe<PartialUser> | undefined
  origin: ActivityFeedOrigin
}> = ({ user, origin }) => {
  const t = useAppTranslations()
  const { firstName, lastName } = user || {}

  const companyLabel =
    user?.company.slug === POWERSPACE_COMPANY_SLUG ? (
      <PowerspaceLogo alt={user.company.name} />
    ) : user?.company.name ? (
      `(${user.company.name})`
    ) : null

  return (
    <Grid
      sx={{ fontWeight: theme.typography.fontWeightBold, paddingLeft: '8px' }}
      size={{
        sm: 2,
      }}
    >
      {origin === ActivityFeedOrigin.System ? (
        <Tooltip title={t('activity.feed.doneAutomaticallyByPlatform')}>
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
            <SettingsSuggestIcon fontSize="small" />
            {t('activity.feed.platform')}
          </Box>
        </Tooltip>
      ) : user ? (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
          {firstName} {lastName}
          {companyLabel ? (
            <Box component="span" sx={{ ml: 0.5, display: 'inline-flex', alignItems: 'center' }}>
              {companyLabel}
            </Box>
          ) : null}
        </Box>
      ) : (
        t('activity.feed.userNotFound')
      )}
    </Grid>
  )
}

const EntitySection: React.FC<{ entityType: EntityType | undefined; entityUrl: string | undefined }> = ({
  entityType,
  entityUrl,
}) => {
  const t = useAppTranslations()

  if (!entityType) {
    return (
      <Grid
        sx={{ overflow: 'hidden' }}
        size={{
          sm: 2,
        }}
      />
    )
  }

  const chip = (
    <Chip
      label={getActivityFeedEntityTypeLabel(entityType, t)}
      clickable={Boolean(entityUrl)}
      sx={{
        backgroundColor: activityFeedConf[entityType]?.backgroundColor,
        color: activityFeedConf[entityType]?.color,
        borderRadius: 1,
      }}
      size="small"
    />
  )

  return (
    <Grid
      sx={{ overflow: 'hidden' }}
      size={{
        sm: 2,
      }}
    >
      {entityUrl ? <UnStyledLink to={entityUrl}>{chip}</UnStyledLink> : chip}
    </Grid>
  )
}

const CompanySection: React.FC<{ company: ActivityFeedGroupPartsFragment['company'] }> = ({ company }) => {
  const companyDashboardUrl = `${CompanyRouteBuilder(company.slug, company.type)}/dashboard`

  return (
    <Grid
      sx={{ textAlign: 'end' }}
      size={{
        sm: 2,
      }}
    >
      <UnStyledLink to={companyDashboardUrl}>{company.name}</UnStyledLink>
    </Grid>
  )
}

const EntityLink: React.FC<{ label: string; url: string | undefined }> = ({ label, url }) => {
  const content = (
    <Box component="span" sx={{ fontWeight: 'bold' }}>
      {label}
    </Box>
  )

  if (!url || !label) return content

  return <UnStyledLink to={url}>{content}</UnStyledLink>
}

const ValueLink: React.FC<{ label: string; url: string | undefined; italic?: boolean }> = ({
  label,
  url,
  italic = false,
}) => {
  if (!label) return null

  const content = italic ? (
    <Box component="span" sx={{ fontStyle: 'italic' }}>
      {label}
    </Box>
  ) : (
    label
  )

  if (!url) return <>{content}</>

  return <UnStyledLink to={url}>{content}</UnStyledLink>
}

const ActivityFeedTypeHeader: React.FC<{
  activityLabel: string
  entityType: EntityType | undefined
  entityLink: React.ReactNode | null
  omitEntity: boolean
}> = ({ activityLabel, entityType, entityLink, omitEntity }) => {
  const t = useAppTranslations()
  const entityTypeLabel = getActivityFeedEntityTypeLabelInSentence(entityType, t)

  return (
    <>
      <Box component="span" sx={{ fontWeight: 'bold' }}>
        {activityLabel}
      </Box>
      {entityTypeLabel ? <> {entityTypeLabel}</> : null}
      {!omitEntity && entityLink ? <> {entityLink}</> : null}
    </>
  )
}

const getActivityTypeLabel = (t: TFunction): Record<ActivityType, string> => ({
  [ActivityType.Add]: t('activity.feed.added'),
  [ActivityType.Create]: t('activity.feed.created'),
  [ActivityType.Delete]: t('activity.feed.deleted'),
  [ActivityType.Invite]: t('activity.feed.invited'),
  [ActivityType.Remove]: t('activity.feed.removed'),
  [ActivityType.Update]: t('activity.feed.updated'),
})

const ActivityFeedItemLine: React.FC<{
  item: ActivityFeedPartsFragment
  groupActivityType: ActivityType
  entityType: EntityType | undefined
  company: ActivityFeedGroupPartsFragment['company']
  indent?: boolean
}> = ({ item, groupActivityType, entityType, company, indent = true }) => {
  const t = useAppTranslations()
  const changedPrefix = (
    <Box component="span" sx={{ fontWeight: 'bold' }}>
      {t('activity.feed.changed')}
    </Box>
  )
  const { property, activityType } = item
  const itemActivityType = activityType ?? groupActivityType
  const itemContext = Object.assign({}, item, { company })
  const propertyLabel = getActivityFeedPropertyLabel(property, t)
  const oldValueDisplay = getActivityFeedValueDisplay(itemContext, 'old', { entityType, t })
  const newValueDisplay = getActivityFeedValueDisplay(itemContext, 'new', { entityType, t })
  const directoryPresetListChange = isDirectoryPresetListProperty(property)
  const exclusionPresetListChange = isExclusionPresetListProperty(property)
  const addRemovePropertyLabel =
    directoryPresetListChange || exclusionPresetListChange
      ? getActivityFeedAddRemovePropertyLabelInSentence(property, t)
      : propertyLabel
  const zoneAssignment = isActivityFeedZoneProperty(property)
    ? parseActivityFeedZoneAssignment(itemActivityType === ActivityType.Remove ? item.oldValue : item.newValue)
    : null
  const lineSx = indent ? { pl: 1.5 } : undefined

  if (isActivityFeedUserPasswordUpdate(itemActivityType, entityType, property))
    return (
      <Box component="div" sx={lineSx}>
        {changedPrefix} {propertyLabel}
      </Box>
    )

  if (isActivityFeedImageUpdate(itemActivityType, property))
    return (
      <Box component="div" sx={lineSx}>
        {changedPrefix} {propertyLabel}
      </Box>
    )

  if (isActivityFeedRedirectUrlUpdate(itemActivityType, property))
    return (
      <Box component="div" sx={lineSx}>
        {changedPrefix}{' '}
        <Tooltip
          title={t('activity.feed.fromOldValueToNewValue', {
            oldValue: oldValueDisplay.label,
            newValue: newValueDisplay.label,
          })}
          slotProps={{
            tooltip: {
              sx: {
                maxWidth: 480,
                wordBreak: 'break-all',
              },
            },
          }}
        >
          <Box component="span" sx={{ textDecoration: 'underline dotted', cursor: 'help' }}>
            {propertyLabel}
          </Box>
        </Tooltip>
      </Box>
    )

  if (['is_active', 'status'].includes(property || ''))
    return (
      <Box component="div" sx={lineSx}>
        {changedPrefix} {propertyLabel}: <StatusValue status={item.newValue} previousStatus={item.oldValue} />
      </Box>
    )

  const geoTargetingTranslationKey = getActivityFeedGeoTargetingTranslationKey(property)
  if (geoTargetingTranslationKey)
    return (
      <Box component="div" sx={lineSx}>
        {t(geoTargetingTranslationKey)}
      </Box>
    )

  if (isActivityFeedPropertyClearedUpdate(itemActivityType, property, item.newValue))
    return (
      <Box component="div" sx={lineSx}>
        {changedPrefix} {propertyLabel}: <ValueLink label={oldValueDisplay.label} url={oldValueDisplay.url} />{' '}
        <Box component="span" sx={{ fontStyle: 'italic' }}>
          ({t('activity.feed.removed').toLowerCase()})
        </Box>
      </Box>
    )

  if (zoneAssignment && (itemActivityType === ActivityType.Add || itemActivityType === ActivityType.Remove)) {
    const templateDisplay = itemActivityType === ActivityType.Add ? newValueDisplay : oldValueDisplay
    const zonePhrase = t(
      itemActivityType === ActivityType.Add
        ? 'activity.feed.addedOnZoneTheTemplate'
        : 'activity.feed.removedFromZoneTheTemplate',
      { zone: zoneAssignment.zone }
    )

    return (
      <Box component="div" sx={lineSx}>
        <Box component="span" sx={{ fontWeight: 'bold' }}>
          {t(itemActivityType === ActivityType.Add ? 'activity.feed.added' : 'activity.feed.removed')}
        </Box>{' '}
        {zonePhrase} <ValueLink italic label={templateDisplay.label} url={templateDisplay.url} />
      </Box>
    )
  }

  if (itemActivityType === ActivityType.Add)
    return (
      <Box component="div" sx={lineSx}>
        <Box component="span" sx={{ fontWeight: 'bold' }}>
          {t('activity.feed.added')}
        </Box>{' '}
        {addRemovePropertyLabel}: <ValueLink italic label={newValueDisplay.label} url={newValueDisplay.url} />
      </Box>
    )

  if (itemActivityType === ActivityType.Remove)
    return (
      <Box component="div" sx={lineSx}>
        <Box component="span" sx={{ fontWeight: 'bold' }}>
          {t('activity.feed.removed')}
        </Box>{' '}
        {addRemovePropertyLabel}: <ValueLink italic label={oldValueDisplay.label} url={oldValueDisplay.url} />
      </Box>
    )

  return (
    <Box component="div" sx={lineSx}>
      {changedPrefix} {propertyLabel}: <ValueLink label={oldValueDisplay.label} url={oldValueDisplay.url} /> →{' '}
      <ValueLink label={newValueDisplay.label} url={newValueDisplay.url} />
    </Box>
  )
}

const ActivityFeedItemList: React.FC<{
  items: ActivityFeedGroupPartsFragment['items']
  groupActivityType: ActivityType
  entityType: EntityType | undefined
  company: ActivityFeedGroupPartsFragment['company']
  indent?: boolean
}> = ({ items, groupActivityType, entityType, company, indent = true }) => (
  <>
    {items.map((item, itemIndex) => (
      <ActivityFeedItemLine
        key={`${item.property ?? 'item'}-${itemIndex}`}
        item={item}
        groupActivityType={groupActivityType}
        entityType={entityType}
        company={company}
        indent={indent}
      />
    ))}
  </>
)

const dedupeGeoTargetingItems = (
  items: ActivityFeedGroupPartsFragment['items']
): ActivityFeedGroupPartsFragment['items'] => {
  const seenTranslationKeys = new Set<string>()

  return items.filter((item) => {
    const translationKey = getActivityFeedGeoTargetingTranslationKey(item.property)
    if (!translationKey) return true
    if (seenTranslationKeys.has(translationKey)) return false

    seenTranslationKeys.add(translationKey)
    return true
  })
}

const TypeSection: React.FC<{
  group: ActivityFeedGroupPartsFragment
  entityUrl: string | undefined
  entityLabel: string
  entityType: EntityType | undefined
  showAllChanges: boolean
}> = ({ group, entityUrl, entityLabel, entityType, showAllChanges }) => {
  const t = useAppTranslations()
  const { activityType, company, items } = group
  const omitEntityFromActivityText = isActivityFeedCompanyEntityType(entityType)
  const entityLink = omitEntityFromActivityText ? null : <EntityLink label={entityLabel} url={entityUrl} />
  const activityTypeLabel = getActivityTypeLabel(t)
  const firstItem = items[0]
  const deleteFallbackLabel =
    entityLabel ||
    (firstItem
      ? getActivityFeedValueDisplay(Object.assign({}, firstItem, { company }), 'old', { entityType, t }).label
      : '')

  let header: React.ReactNode

  if (activityType === ActivityType.Invite)
    header = (
      <>
        <Box component="span" sx={{ fontWeight: 'bold' }}>
          {activityTypeLabel[ActivityType.Invite]}
        </Box>{' '}
        {t('commons.user')} <EntityLink label={entityLabel} url={entityUrl} />{' '}
        {t('activity.feed.toJoinCompany', { companyName: company.name })}
      </>
    )
  else if (activityType === ActivityType.Create)
    header = (
      <ActivityFeedTypeHeader
        activityLabel={activityTypeLabel[ActivityType.Create]}
        entityType={entityType}
        entityLink={entityLink}
        omitEntity={omitEntityFromActivityText}
      />
    )
  else if (activityType === ActivityType.Delete)
    header = (
      <ActivityFeedTypeHeader
        activityLabel={activityTypeLabel[ActivityType.Delete]}
        entityType={entityType}
        entityLink={<EntityLink label={deleteFallbackLabel} url={entityUrl} />}
        omitEntity={omitEntityFromActivityText}
      />
    )
  else
    header = (
      <ActivityFeedTypeHeader
        activityLabel={activityTypeLabel[ActivityType.Update]}
        entityType={entityType}
        entityLink={entityLink}
        omitEntity={omitEntityFromActivityText}
      />
    )

  const isNativeTemplateItem = (item: ActivityFeedGroupPartsFragment['items'][number]): boolean =>
    isActivityFeedAdTemplateNativeTemplateUpdate(item.activityType ?? activityType, entityType, item.property)

  const visibleItems = dedupeGeoTargetingItems(items.filter((item) => !isNativeTemplateItem(item)))
  const isAddOrRemoveGroup =
    activityType === ActivityType.Add ||
    activityType === ActivityType.Remove ||
    visibleItems.some((item) => item.activityType === ActivityType.Add || item.activityType === ActivityType.Remove)
  const showItemList =
    visibleItems.length > 0 &&
    (isAddOrRemoveGroup ||
      activityType === ActivityType.Update ||
      visibleItems.some((item) => item.activityType === ActivityType.Update))
  const inlineItems = showAllChanges ? visibleItems : visibleItems.slice(0, ACTIVITY_FEED_MAX_VISIBLE_ITEM_CHANGES)
  const hasOverflowingChanges = !showAllChanges && visibleItems.length > ACTIVITY_FEED_MAX_VISIBLE_ITEM_CHANGES

  return (
    <Grid
      size={{
        sm: 4,
      }}
    >
      {header}
      {showItemList ? (
        <Box component="div" sx={{ mt: 0.5 }}>
          <ActivityFeedItemList
            items={inlineItems}
            groupActivityType={activityType}
            entityType={entityType}
            company={company}
          />
          {hasOverflowingChanges ? (
            <Tooltip
              slotProps={activityFeedChangesTooltipSlotProps}
              title={
                <Box component="div" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, py: 0.5 }}>
                  <ActivityFeedItemList
                    items={visibleItems}
                    groupActivityType={activityType}
                    entityType={entityType}
                    company={company}
                    indent={false}
                  />
                </Box>
              }
            >
              <Button
                size="small"
                sx={{
                  display: 'block',
                  ml: 1.5,
                  mt: 0.25,
                  minWidth: 0,
                  px: 0,
                  textTransform: 'none',
                }}
                variant="text"
              >
                {t('activity.feed.seeAllChanges')}
              </Button>
            </Tooltip>
          ) : null}
        </Box>
      ) : null}
    </Grid>
  )
}

const getCommonsStatusTranslationKey = (status: string | null | undefined): string => {
  if (status === 'true') return 'commons.active'
  if (status === 'false') return 'commons.inactive'
  return `commons.${(status ?? '').replace(/_/g, '').toLowerCase()}`
}

const StatusValue: React.FC<{ status: string | null | undefined; previousStatus?: string | null }> = ({
  status,
  previousStatus,
}) => {
  const t = useAppTranslations()

  const statusColor = (): string => {
    switch (status) {
      case 'true':
      case 'active':
        return 'success.main'
      case 'false':
      case 'inactive':
        return 'warning.main'
      case 'pending':
        return 'info.main'
      case 'rejected':
        return 'error.main'
      default:
        return ''
    }
  }

  const content = (
    <Box
      component="span"
      sx={{
        color: statusColor(),
        fontWeight: 'bold',
      }}
    >
      {t(getCommonsStatusTranslationKey(status))}
    </Box>
  )

  if (!previousStatus) return content

  return (
    <Tooltip title={`${t('activity.feed.from')} ${t(getCommonsStatusTranslationKey(previousStatus))}`}>
      <Box component="span">{content}</Box>
    </Tooltip>
  )
}

const DateSection: React.FC<{ date: string }> = ({ date }) => (
  <Grid
    sx={{ textAlign: 'end', paddingRight: 1 }}
    size={{
      sm: 2,
    }}
  >
    {displayDateAsText(date, DATE_FORMAT)}
  </Grid>
)
